<template>
  <!--
    Inline version of SkillSettingsModal — renders the variant_key
    options as cards directly in the page (no modal). Triggered by a
    "select-box"-style header that shows the current variant. Click
    the header → cards reveal below; click an option → save flips the
    variant_key. Same backend (update-variant-key) as the modal.
  -->
  <div class="skill-settings" v-click-outside="closePopover">
    <button class="select-box" :class="{ open }" @click="open = !open">
      <span class="label">{{ currentDisplayName }}</span>
      <span class="chev">▾</span>
    </button>

    <!-- Popover overlay — floats above siblings so the variant
         cards don't reflow the page when expanded. -->
    <div v-if="open" class="variant-popover">
      <div v-if="loading" class="status">Loading options…</div>
      <div v-else-if="!variants.length" class="status">
        No variants available for this skill.
      </div>
      <button
        v-else
        v-for="v in variants"
        :key="v.key"
        type="button"
        class="variant-card"
        :class="{ current: v.key === variant.variant_key }"
        :disabled="saving"
        @click="onPick(v.key)"
      >
        <span class="radio" :class="{ on: v.key === variant.variant_key }"></span>
        <div class="content">
          <div class="row">
            <h3 class="name">{{ v.display_name }}</h3>
            <span v-if="v.key === variant.variant_key" class="current-tag">Current</span>
            <span v-else-if="v.is_wizard_default" class="default-tag">Default</span>
          </div>
          <p class="desc">{{ v.description }}</p>
        </div>
      </button>
      <p v-if="saveError" class="error">⚠ {{ saveError }}</p>
      <!-- Snapshots — one timeline of restorable configs (published + switches). -->
      <div class="popover-foot">
        <button type="button" class="game-history-link" @click="onSnapshots">
          ⟲ Snapshots
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import * as api from '@/api-service.js'

// Local v-click-outside directive — closes the popover when the
// operator clicks anywhere else. Avoids pulling in a dep for a
// single use.
const vClickOutside = {
  mounted(el, binding) {
    el._clickOutside = (event) => {
      if (!el.contains(event.target)) binding.value()
    }
    document.addEventListener('mousedown', el._clickOutside)
  },
  unmounted(el) {
    document.removeEventListener('mousedown', el._clickOutside)
  },
}

const props = defineProps({
  variant: { type: Object, required: true },
  // When true, picking a variant does NOT call the API. The component
  // emits 'pick-requested' with the chosen key + variant; the parent
  // decides whether to confirm (e.g. show a Save/Switch modal) and
  // call api.updateVariantKey itself. Default false keeps the legacy
  // auto-save behavior for existing consumers (SkillsEditorView).
  confirmSwitch: { type: Boolean, default: false },
})
const emit = defineEmits(['saved', 'pick-requested', 'snapshots'])

function onSnapshots() {
  open.value = false
  emit('snapshots')
}

const open = ref(false)
const loading = ref(true)
const variants = ref([])
const saving = ref(false)
const saveError = ref('')

const currentDisplayName = computed(() => {
  const match = variants.value.find(v => v.key === props.variant.variant_key)
  if (match) return match.display_name
  // Fallback while options load — humanize the variant_key.
  const key = props.variant.variant_key || ''
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
})

onMounted(async () => {
  loading.value = true
  try {
    const skills = await api.fetchAvailableVariants()
    const match = skills.find(s => s.key === props.variant.skill_key)
    variants.value = match?.variants || []
  } finally {
    loading.value = false
  }
})

function closePopover() {
  open.value = false
}

async function onPick(key) {
  if (key === props.variant.variant_key) {
    open.value = false
    return
  }
  // Confirm-gated mode: hand the pick up to the parent so it can show
  // its Save/Switch modal before any API call fires. No auto-save.
  if (props.confirmSwitch) {
    open.value = false
    emit('pick-requested', { key, variant: props.variant })
    return
  }
  saving.value = true
  saveError.value = ''
  try {
    const refreshed = await api.updateVariantKey(props.variant.id, key)
    emit('saved', refreshed)
    open.value = false
  } catch (e) {
    saveError.value = e?.response?.data?.error || String(e)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.skill-settings {
  position: relative;
  display: inline-flex;
}
.select-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(195, 169, 255, 0.22);
  border-radius: 8px;
  color: #C9A94E;
  font-family: 'Cinzel', serif;
  font-size: 13px;
  letter-spacing: 0.04em;
  cursor: pointer;
  min-width: 160px;
  transition: all 0.18s;
}
.select-box:hover {
  border-color: #C9A94E;
  background: rgba(0, 0, 0, 0.45);
}
.select-box .chev {
  font-size: 11px;
  transition: transform 0.18s;
}
.select-box.open .chev { transform: rotate(180deg); }

.variant-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 320px;
  max-width: 420px;
  padding: 12px;
  background: #1a0c2e;
  border: 1px solid rgba(195, 169, 255, 0.32);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55),
              0 0 22px rgba(201, 169, 78, 0.1);
}
.status {
  text-align: center;
  padding: 16px;
  color: rgba(212, 196, 232, 0.55);
  font-style: italic;
  font-size: 13px;
}
.variant-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: rgba(255,255,255,0.03);
  border: 2px solid rgba(195,169,255,0.18);
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font-family: inherit;
  transition: all 0.18s;
}
.variant-card:hover:not(:disabled) {
  background: rgba(255,255,255,0.06);
  border-color: rgba(195,169,255,0.4);
}
.variant-card:disabled { opacity: 0.6; cursor: wait; }
.variant-card.current { border-color: rgba(201,169,78,0.5); }
.radio {
  width: 18px; height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(195,169,255,0.45);
  flex-shrink: 0;
  margin-top: 3px;
  position: relative;
}
.radio.on { border-color: #C9A94E; }
.radio.on::after {
  content: '';
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #C9A94E;
}
.content { flex: 1; min-width: 0; }
.row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
.name {
  font-family: 'Cinzel', serif;
  color: #E8D8FF;
  font-size: 14px;
  margin: 0;
  font-weight: 600;
}
.current-tag, .default-tag {
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 10px;
}
.current-tag {
  color: #C9A94E;
  background: rgba(201,169,78,0.18);
  border: 1px solid rgba(201,169,78,0.45);
}
.default-tag {
  color: rgba(195,169,255,0.85);
  background: rgba(195,169,255,0.08);
  border: 1px solid rgba(195,169,255,0.22);
}
.desc {
  margin: 0;
  color: rgba(212, 196, 232, 0.7);
  font-size: 12px;
  line-height: 1.4;
}
.error {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  text-align: center;
  margin: 4px 0 0;
}
.popover-foot {
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.game-history-link {
  width: 100%;
  background: transparent;
  border: none;
  color: rgba(195, 169, 255, 0.8);
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-align: left;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
}
.game-history-link:hover { color: #C9A94E; background: rgba(255, 255, 255, 0.04); }
</style>
